import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';

const STATUS_LABELS = {
  new: 'New',
  reviewed: 'Reviewed',
};

const PAGE_SIZE = 10;

const AdminApplicantsPage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total: 0, page_size: PAGE_SIZE });

  const statuses = ['All', 'new', 'reviewed'];

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchApplications(page);
  }, [statusFilter, page]);

  const fetchApplications = async (targetPage = 1) => {
    setLoading(true);
    try {
      let query = supabase
        .from('job_applications')
        .select('*', { count: 'exact' })
        .order('submitted_at', { ascending: false });

      if (statusFilter !== 'All') {
        query = query.eq('status', statusFilter);
      }

      if (search.trim()) {
        query = query.or(`full_name.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%`);
      }

      const from = (targetPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data, count, error } = await query;

      if (error) throw error;

      setApplications(data || []);
      const totalPages = Math.ceil((count || 0) / PAGE_SIZE);
      setMeta({
        page: targetPage,
        total_pages: totalPages,
        total: count || 0,
        page_size: PAGE_SIZE,
      });
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchApplications(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      reviewed: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex flex-1 gap-2">
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="bg-red-600 hover:bg-red-700">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-2 overflow-x-auto">
              {statuses.map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  {status === 'All' ? 'All' : STATUS_LABELS[status]}
                </Button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Applicant</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Job Title</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Submitted</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.length > 0 ? (
                      applications.map((app) => (
                        <tr key={app.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{app.full_name}</p>
                              <p className="text-sm text-gray-500">{app.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{app.job_title}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}
                            >
                              {STATUS_LABELS[app.status] || app.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{formatDate(app.submitted_at)}</td>
                          <td className="py-3 px-4">
                            <Link
                              to={`/admin/applications/${app.id}`}
                              className="text-red-600 hover:text-red-700 text-sm font-medium"
                            >
                              View Details
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-12 text-center text-gray-500">
                          No applications found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-500">
                  {meta.total > 0 ? (
                    <>
                      Showing {(meta.page - 1) * meta.page_size + 1}-
                      {Math.min(meta.page * meta.page_size, meta.total)} of {meta.total} applications
                    </>
                  ) : (
                    'No applications to display'
                  )}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={meta.page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPage((prev) => Math.min(prev + 1, meta.total_pages))}
                    disabled={meta.page >= meta.total_pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminApplicantsPage;
